import { useState, useEffect, useCallback, useRef } from 'react';

const HOVER_CLEAR_DELAY_MS = 80;

const INSERT_ZONE_SELECTOR =
  '.handles-layer__edge-insert-zone, .handles-layer__edge-insert';

/**
 * Hook for managing edge hover and menu interactions
 */
export function useEdgeInteraction({ containerRef, canvasToScreen, onInsertNode }) {
  const [hoveredEdgeId, setHoveredEdgeId] = useState(null);
  const [menuEdge, setMenuEdge] = useState(null);
  const [menuPos, setMenuPos] = useState(null);
  const clearHoverTimeoutRef = useRef(null);

  const cancelScheduledClear = useCallback(() => {
    if (clearHoverTimeoutRef.current != null) {
      clearTimeout(clearHoverTimeoutRef.current);
      clearHoverTimeoutRef.current = null;
    }
  }, []);

  const scheduleClearHover = useCallback(() => {
    cancelScheduledClear();
    clearHoverTimeoutRef.current = setTimeout(() => {
      clearHoverTimeoutRef.current = null;
      setHoveredEdgeId(null);
    }, HOVER_CLEAR_DELAY_MS);
  }, [cancelScheduledClear]);

  useEffect(() => () => cancelScheduledClear(), [cancelScheduledClear]);

  useEffect(() => {
    const container = containerRef?.current;
    if (!container) return;

    const handleMouseOver = (e) => {
      const hitbox = e.target.closest('.flowchart__edge-hitbox');
      if (hitbox) {
        const edgeGroup = hitbox.closest('.flowchart__edge');
        if (edgeGroup) {
          const classes = edgeGroup.className.baseVal || edgeGroup.className;
          const match = classes.match(/flowchart__edge--id-([^\s]+)/);
          if (match) {
            cancelScheduledClear();
            setHoveredEdgeId(match[1]);
          }
        }
      }
    };

    const handleMouseOut = (e) => {
      const hitbox = e.target.closest('.flowchart__edge-hitbox');
      if (!hitbox) return;

      const relatedTarget = e.relatedTarget;
      if (relatedTarget?.closest?.(INSERT_ZONE_SELECTOR)) {
        return;
      }
      scheduleClearHover();
    };

    container.addEventListener('mouseover', handleMouseOver);
    container.addEventListener('mouseout', handleMouseOut);

    return () => {
      container.removeEventListener('mouseover', handleMouseOver);
      container.removeEventListener('mouseout', handleMouseOut);
    };
  }, [containerRef, cancelScheduledClear, scheduleClearHover]);

  const handleEdgeInsertClick = useCallback((edge, midpoint, e) => {
    e.stopPropagation();

    const screenPos = canvasToScreen(midpoint.x, midpoint.y);
    const rect = containerRef?.current?.getBoundingClientRect();
    if (rect) {
      setMenuPos({ x: rect.left + screenPos.x, y: rect.top + screenPos.y });
      setMenuEdge({ edge, midpoint });
    }
  }, [canvasToScreen, containerRef]);

  const handleSelectNodeType = useCallback((nodeType) => {
    if (menuEdge) {
      onInsertNode?.(menuEdge.edge, nodeType, menuEdge.midpoint);
    }
    setMenuEdge(null);
    setMenuPos(null);
  }, [menuEdge, onInsertNode]);

  const handleCloseMenu = useCallback(() => {
    setMenuEdge(null);
    setMenuPos(null);
  }, []);

  const handleInsertMouseEnter = useCallback((edgeId) => {
    cancelScheduledClear();
    setHoveredEdgeId(edgeId);
  }, [cancelScheduledClear]);

  const handleButtonMouseLeave = useCallback((e) => {
    const relatedTarget = e.relatedTarget;
    if (relatedTarget?.closest?.('.flowchart__edge-hitbox, .handles-layer__edge-insert-zone')) {
      return;
    }
    scheduleClearHover();
  }, [scheduleClearHover]);

  return {
    hoveredEdgeId,
    menuEdge,
    menuPos,
    handleEdgeInsertClick,
    handleSelectNodeType,
    handleCloseMenu,
    handleInsertMouseEnter,
    handleButtonMouseLeave,
  };
}

export default useEdgeInteraction;
